import CheckIcon from '@mui/icons-material/Check';
import { Box, ButtonBase, LinearProgress, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { FormStep } from '../lib/form-steps';
import { tr } from '../lib/i18n';

const labelOf = (s: FormStep, i: number) => s.title || tr(`الخطوة ${i + 1}`, `Step ${i + 1}`);

/** Desktop: the steps as a vertical list (same look as the public form). */
export function FormStepNav({ steps, activeIndex, maxReachableIndex, errorSteps, onSelect }: { steps: FormStep[]; activeIndex: number; maxReachableIndex: number; errorSteps?: Set<number>; onSelect: (i: number) => void }) {
  const m3 = useTheme().palette.m3;
  return (
    <Box component="nav" aria-label={tr('خطوات النموذج', 'Form steps')} sx={{ bgcolor: '#fff', border: 1, borderColor: 'divider', borderRadius: '24px', p: 1.5 }}>
      <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0 }}>
        {steps.map((s, i) => {
          const active = i === activeIndex;
          const done = i < activeIndex;
          const locked = i > maxReachableIndex;
          const bad = errorSteps?.has(i);
          const last = i === steps.length - 1;
          return (
            <Box component="li" key={s.id} sx={{ position: 'relative' }}>
              {!last && <Box aria-hidden sx={{ position: 'absolute', insetInlineStart: 25, top: 44, bottom: -4, width: 2, borderRadius: 1, bgcolor: done ? 'primary.main' : 'divider' }} />}
              <ButtonBase
                disabled={locked}
                onClick={() => onSelect(i)}
                aria-current={active ? 'step' : undefined}
                sx={{
                  width: '100%',
                  justifyContent: 'flex-start',
                  gap: 1.5,
                  p: 1,
                  mb: last ? 0 : 1,
                  borderRadius: 999,
                  textAlign: 'start',
                  bgcolor: active ? m3.primaryContainer : 'transparent',
                  '&:not(.Mui-disabled):hover': { bgcolor: active ? m3.primaryContainer : m3.surfaceContainerLow },
                  '&.Mui-disabled': { opacity: 0.5 },
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    flexShrink: 0,
                    display: 'grid',
                    placeItems: 'center',
                    fontSize: 14,
                    fontWeight: 600,
                    position: 'relative',
                    zIndex: 1,
                    bgcolor: bad ? 'error.main' : done || active ? 'primary.main' : '#fff',
                    color: bad || done || active ? '#fff' : 'text.secondary',
                    border: bad || done || active ? 0 : 1.5,
                    borderColor: 'divider',
                  }}
                >
                  {done && !bad ? <CheckIcon sx={{ fontSize: 18 }} /> : i + 1}
                </Box>
                <Typography variant="body2" sx={{ fontWeight: active ? 600 : 400, color: bad ? 'error.main' : active ? m3.onPrimaryContainer : 'text.primary' }}>
                  {labelOf(s, i)}
                </Typography>
              </ButtonBase>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

/** Phones / narrow screens: compact progress at the top of the form card. */
export function MobileProgress({ steps, activeIndex }: { steps: FormStep[]; activeIndex: number }) {
  const next = steps[activeIndex + 1];
  return (
    <Box sx={{ display: { md: 'none' }, mb: 2.5 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1 }}>
        <Typography variant="caption" color="primary.main" fontWeight={600}>
          {tr(`الخطوة ${activeIndex + 1} من ${steps.length}`, `Step ${activeIndex + 1} of ${steps.length}`)}
        </Typography>
        {next && (
          <Typography variant="caption" color="text.secondary">
            {tr('التالي:', 'Next:')} {labelOf(next, activeIndex + 1)}
          </Typography>
        )}
      </Stack>
      <LinearProgress variant="determinate" value={((activeIndex + 1) / steps.length) * 100} sx={{ height: 6, borderRadius: 3 }} />
    </Box>
  );
}
